import React from 'react'
import { cn } from '../../lib/utils'

interface BadgeProps {
  children: React.ReactNode
  variant?: 'default' | 'secondary' | 'destructive' | 'outline' | 'success' | 'warning'
  size?: 'sm' | 'md' | 'lg'
  className?: string
}

export const Badge: React.FC<BadgeProps> = ({
  children,
  variant = 'default',
  size = 'md',
  className,
}) => {
  const variantClasses = {
    default: 'badge-default',
    secondary: 'badge-secondary',
    destructive: 'badge-destructive',
    outline: 'badge-outline',
    success: 'bg-green-100 text-green-800 border-green-200',
    warning: 'bg-yellow-100 text-yellow-800 border-yellow-200',
  }

  const sizeClasses = {
    sm: 'text-xs px-2 py-0.5',
    md: 'text-xs px-2.5 py-0.5',
    lg: 'text-sm px-3 py-1',
  }

  return (
    <span
      className={cn(
        'badge',
        variantClasses[variant],
        sizeClasses[size],
        className
      )}
    >
      {children}
    </span>
  )
}

// Status chip — Darwin Design System "Status" component (matches sop-studio
// StatusBadge). Small 2px-radius chip with a leading icon + 12px uppercase
// label. DS palette: in-progress = teal, done = chartreuse green, error = red,
// warning = orange, neutral = gray.
export type StatusVariant = 'done' | 'progress' | 'warning' | 'error' | 'neutral'

const STATUS_VARIANT_STYLES: Record<StatusVariant, string> = {
  done: 'bg-green-100 text-green-600',
  progress: 'bg-blue-100 text-blue-600',
  warning: 'bg-orange-100 text-orange-600',
  error: 'bg-red-100 text-red-700',
  neutral: 'bg-gray-200 text-gray-700',
}

function StatusIcon({ variant }: { variant: StatusVariant }) {
  const common = {
    viewBox: '0 0 16 16',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 1.5,
    className: 'h-3 w-3 shrink-0',
    'aria-hidden': true,
  } as const

  if (variant === 'done') {
    // check-circle (DS "closed/done")
    return (
      <svg {...common}>
        <circle cx="8" cy="8" r="6.25" />
        <path d="M5.25 8.25 7 10 10.75 6" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    )
  }
  if (variant === 'error') {
    // no-entry / prohibited circle (DS "denied")
    return (
      <svg {...common}>
        <circle cx="8" cy="8" r="6.25" />
        <path d="M3.6 3.6 12.4 12.4" strokeLinecap="round" />
      </svg>
    )
  }
  if (variant === 'neutral') {
    // plain circle (DS "not started")
    return (
      <svg {...common}>
        <circle cx="8" cy="8" r="6.25" />
      </svg>
    )
  }
  // contrast / half-filled circle (DS "in progress" — also used for warning)
  return (
    <svg {...common}>
      <circle cx="8" cy="8" r="6.25" />
      <path d="M8 1.75A6.25 6.25 0 0 1 8 14.25Z" fill="currentColor" stroke="none" />
    </svg>
  )
}

// Generic DS chip primitive — same visual as StatusBadge but with a free
// label (for table verdict cells like Yes / Partially / No).
interface StatusChipProps {
  variant: StatusVariant
  showIcon?: boolean
  className?: string
  children: React.ReactNode
}

export const StatusChip: React.FC<StatusChipProps> = ({
  variant,
  showIcon = true,
  className,
  children,
}) => (
  <span
    className={cn(
      'inline-flex items-center justify-center gap-1 rounded-sm p-[5px] text-[12px] font-normal uppercase leading-none whitespace-nowrap',
      STATUS_VARIANT_STYLES[variant],
      className
    )}
  >
    {showIcon && <StatusIcon variant={variant} />}
    {children}
  </span>
)

interface StatusBadgeProps {
  status: string
  className?: string
}

export const StatusBadge: React.FC<StatusBadgeProps> = ({
  status,
  className,
}) => {
  const getStatusVariant = (status: string): StatusVariant => {
    switch (status.toLowerCase()) {
      case 'active':
      case 'completed':
      case 'approved':
      case 'meets':
      case 'submitted':
      case 'evaluated':
        return 'done'
      case 'pending':
      case 'in_progress':
      case 'analyzing':
      case 'running':
      case 'processing':
      case 'uploading':
      case 'queued':
      case 'scoring':
        return 'progress'
      case 'partially_meets':
      case 'ready_for_review':
        return 'warning'
      case 'failed':
      case 'rejected':
      case 'not_met':
      case 'error':
        return 'error'
      case 'draft':
      case 'not_evaluated':
      case 'closed':
      case 'cancelled':
        return 'neutral'
      default:
        return 'neutral'
    }
  }

  return (
    <StatusChip variant={getStatusVariant(status)} className={className}>
      {status.replace(/_/g, ' ')}
    </StatusChip>
  )
}

// PriorityTag — "Short" variant of the Darwin Risk/Priority tag (matches
// taskline priority-tag.tsx, Figma node 4817-3784). An uppercase label above
// a severity meter underline whose width and color grow with the level.
type PriorityLevel = 'critical' | 'high' | 'medium' | 'low'

// text color · bar color · meter fill (% of label width) — Darwin ramps.
const PRIORITY_CONFIG: Record<PriorityLevel, { text: string; bar: string; fill: string }> = {
  critical: { text: 'text-red-700', bar: 'bg-red-800', fill: 'w-full' },
  high: { text: 'text-red-600', bar: 'bg-red-600', fill: 'w-3/4' },
  medium: { text: 'text-orange-600', bar: 'bg-orange-600', fill: 'w-1/2' },
  low: { text: 'text-gray-700', bar: 'bg-gray-400', fill: 'w-1/4' },
}

const normalizePriority = (priority: string): PriorityLevel => {
  switch (priority.toLowerCase()) {
    case 'critical':
    case 'urgent':
      return 'critical'
    case 'high':
      return 'high'
    case 'medium':
      return 'medium'
    default:
      return 'low'
  }
}

interface PriorityTagProps {
  priority: string
  className?: string
}

export const PriorityTag: React.FC<PriorityTagProps> = ({ priority, className }) => {
  const config = PRIORITY_CONFIG[normalizePriority(priority)]
  return (
    <div className={cn('inline-flex flex-col items-start gap-1 px-1 py-[3px]', className)}>
      <span className={cn('text-[11px] font-semibold uppercase leading-none whitespace-nowrap', config.text)}>
        {priority.replace(/_/g, ' ')}
      </span>
      <div className={cn('h-[2px] rounded-[1px]', config.bar, config.fill)} />
    </div>
  )
}

// Back-compat alias — existing pages render priorities through PriorityBadge.
export const PriorityBadge: React.FC<PriorityTagProps> = PriorityTag

// Count badge component
interface CountBadgeProps {
  count: number
  max?: number
  className?: string
}

export const CountBadge: React.FC<CountBadgeProps> = ({
  count,
  max = 99,
  className,
}) => {
  const displayCount = count > max ? `${max}+` : count.toString()
  
  return (
    <Badge
      variant="destructive"
      size="sm"
      className={cn('ml-1', className)}
    >
      {displayCount}
    </Badge>
  )
}

export default Badge